<template>
  <section v-show="active" class="app-modal__section">
    <header class="app-modal__section-head app-modal__section-head--split">
      <div class="expense-section-head-copy">
        <h4>{{ text.sections.general.title }}</h4>
        <p>{{ text.sections.general.subtitle }}</p>
      </div>

      <div class="expense-readonly-status" role="status" aria-live="polite">
        <strong>{{ text.sections.general.statusPrefix }} {{ resolvedStatusLabel.toLowerCase() }}</strong>
        <strong class="expense-approval-status-line">
          <span>{{ approvalLabelPrefix }}</span>
          <label
            v-if="showApprovalCheckbox"
            class="app-modal-toggle expense-approval-toggle"
          >
            <input
              :checked="approvalChecked"
              type="checkbox"
              :disabled="isGeneralReadOnly"
              @change="toggleApproval($event)"
            />
          </label>
          <span>{{ approvalStateLabel }}</span>
        </strong>
      </div>
    </header>

    <div class="app-modal__grid two-columns">
      <div class="app-modal-field">
        <span>{{ text.sections.general.voucherType }} *</span>
        <multiselect
          v-model="voucherTypeModel"
          :options="options.documentTypes"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="false"
          :allow-empty="false"
          :disabled="isGeneralReadOnly"
          class="app-modal-select"
        >
          <template #noResult><span>{{ text.sections.common.noOption }}</span></template>
          <template #noOptions><span>{{ text.sections.common.noOption }}</span></template>
        </multiselect>
      </div>

      <div class="app-modal-field">
        <div class="expense-inline-label-row">
          <span>{{ text.sections.general.supplier }} *</span>
          <button type="button" class="expense-inline-link" :disabled="isGeneralReadOnly" @click="toggleSupplierInlineForm">
            {{ showSupplierInlineForm ? text.sections.general.buttons.hide : text.sections.general.buttons.newSupplier }}
          </button>
        </div>

        <multiselect
          v-model="form.supplier"
          :options="options.suppliers"
          data-focus-field="supplier"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          :disabled="isGeneralReadOnly"
          :class="['app-modal-select', { 'is-invalid': hasFieldError('supplier') }]"
          :placeholder="text.sections.general.placeholders.supplier"
          @close="touchField('supplier')"
        >
          <template #option="{ option }">
            <div class="expense-supplier-option">
              <strong>{{ option?.meta?.name || option.label }}</strong>
              <small v-if="option?.meta?.class_name || option?.meta?.category_name">
                {{ option?.meta?.class_name || '-' }} / {{ option?.meta?.category_name || '-' }}
              </small>
            </div>
          </template>
          <template #singleLabel="{ option }">
            <div class="expense-supplier-option">
              <span>{{ option?.meta?.name || option.label }}</span>
            </div>
          </template>
          <template #noResult><span>{{ text.sections.common.noOption }}</span></template>
          <template #noOptions><span>{{ text.sections.common.noOption }}</span></template>
        </multiselect>
        <div class="invalid-feedback d-block" v-if="hasFieldError('supplier')">
          <span v-if="v$.form.supplier.$errors.length">
            {{ v$.form.supplier.$errors[0].$message }}
          </span>
        </div>

        <div v-if="showSupplierInlineForm" class="expense-inline-form">
          <label class="app-modal-field">
            <span>{{ text.sections.general.supplierName }} *</span>
            <input
              v-model.trim="newSupplierName"
              type="text"
              class="form-control"
              :disabled="isGeneralReadOnly"
              :placeholder="text.sections.general.placeholders.supplierName"
            />
          </label>

          <div class="expense-inline-actions">
            <button type="button" class="btn btn-light" :disabled="isGeneralReadOnly" @click="cancelSupplierInlineForm">{{ text.sections.general.buttons.cancel }}</button>
            <button
              type="button"
              class="app-header-btn app-header-btn--primary"
              :disabled="isGeneralReadOnly || creatingSupplier || newSupplierName === ''"
              @click="saveSupplierInline"
            >
              {{ text.sections.general.buttons.saveAndUseSupplier }}
            </button>
          </div>
        </div>
      </div>

    </div>

    <div v-if="showSupplierContributorsSelector" class="app-modal__grid two-columns">
      <div class="app-modal-field full-width">
        <span>{{ text.sections.general.supplierContributor }}</span>
        <multiselect
          v-model="form.supplierContributor"
          :options="options.supplierContributors"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          :disabled="isGeneralReadOnly"
          class="app-modal-select"
          :placeholder="text.sections.general.placeholders.supplierContributor"
          :loading="supplierContributorsLoading"
        >
          <template #noResult><span>{{ text.sections.common.noOption }}</span></template>
          <template #noOptions><span>{{ text.sections.common.noOption }}</span></template>
        </multiselect>
      </div>
    </div>

    <div class="expense-class-category-row">
      <AppClassCategorySelector
        :class-option="form.expenseClass"
        :category-option="form.expenseCategory"
        :disabled="isClassCategoryReadOnly"
        @update:classOption="form.expenseClass = $event"
        @update:categoryOption="form.expenseCategory = $event"
      />
    </div>

    <section class="expense-modal-box">
      <header class="expense-modal-box__head">
        <div>
          <h4>{{ text.sections.general.amountsTitle }}</h4>
          <p>{{ text.sections.general.amountsSubtitle }}</p>
        </div>
        <AppCurrencyControl
          v-model="currencyModel"
          :exchange-rate="form.exchangeRate"
          :disabled="isGeneralReadOnly"
          @update:exchange-rate="form.exchangeRate = $event"
        />
      </header>

      <div v-if="!isEditMode" class="expense-frequency-box">
        <span class="expense-frequency-box__label">{{ text.sections.general.frequencyTitle }}</span>
        <div class="expense-frequency-row">
          <label
            v-for="option in frequencyCheckboxOptions"
            :key="option.value"
            class="app-modal-toggle expense-frequency-toggle"
          >
            <input
              type="checkbox"
              :checked="selectedFrequency === option.value"
              :disabled="isGeneralReadOnly"
              @change="selectFrequency(option.value)"
            />
            <span>{{ option.label }}</span>
          </label>
        </div>

        <div v-if="showRecurringFields" class="app-modal__grid two-columns expense-recurring-grid">
          <label class="app-modal-field expense-recurring-count">
            <span>{{ text.sections.general.expensesCount }}</span>
            <input
              v-model.number="form.expensesCount"
              data-focus-field="expensesCount"
              type="number"
              min="1"
              step="1"
              class="form-control expense-recurring-count__input"
              :class="{ 'is-invalid': hasFieldError('expensesCount') }"
              :disabled="isGeneralReadOnly"
              :placeholder="text.sections.general.placeholders.expensesCount"
              @blur="touchField('expensesCount')"
            />
            <div class="invalid-feedback d-block" v-if="hasFieldError('expensesCount')">
              <span v-if="v$.form.expensesCount.$errors.length">
                {{ v$.form.expensesCount.$errors[0].$message }}
              </span>
            </div>
          </label>

          <div v-if="showMonthlyFields" class="app-modal-field">
            <span>{{ text.sections.general.firstInstallment }}</span>
            <multiselect
              ref="firstInstallmentSelect"
              v-model="form.firstInstallmentOption"
              data-focus-field="firstInstallmentOption"
              :options="firstInstallmentOptions"
              track-by="value"
              label="label"
              :show-labels="false"
              :searchable="true"
              :allow-empty="true"
              :max-height="152"
              open-direction="down"
              :disabled="isGeneralReadOnly"
              :class="['app-modal-select', { 'is-invalid': hasFieldError('firstInstallmentOption') }]"
              :placeholder="text.sections.general.placeholders.firstInstallment"
              @open="focusSelectedFirstInstallment"
              @close="touchField('firstInstallmentOption')"
            >
              <template #noResult><span>{{ text.sections.common.noOption }}</span></template>
              <template #noOptions><span>{{ text.sections.common.noOption }}</span></template>
            </multiselect>
            <div class="invalid-feedback d-block" v-if="hasFieldError('firstInstallmentOption')">
              <span v-if="v$.form.firstInstallmentOption.$errors.length">
                {{ v$.form.firstInstallmentOption.$errors[0].$message }}
              </span>
            </div>
          </div>

          <div v-if="showMultipleFields" class="app-modal-field">
            <span>{{ text.sections.general.periodicity }}</span>
            <multiselect
              v-model="form.periodicityOption"
              data-focus-field="periodicityOption"
              :options="options.periodicities"
              track-by="value"
              label="label"
              :show-labels="false"
              :searchable="true"
              :allow-empty="true"
              :disabled="isGeneralReadOnly"
              :class="['app-modal-select', { 'is-invalid': hasFieldError('periodicityOption') }]"
              :placeholder="text.sections.general.placeholders.periodicity"
              @close="touchField('periodicityOption')"
            >
              <template #noResult><span>{{ text.sections.common.noOption }}</span></template>
              <template #noOptions><span>{{ text.sections.common.noOption }}</span></template>
            </multiselect>
            <div class="invalid-feedback d-block" v-if="hasFieldError('periodicityOption')">
              <span v-if="v$.form.periodicityOption.$errors.length">
                {{ v$.form.periodicityOption.$errors[0].$message }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="app-modal__grid two-columns">
        <label class="app-modal-field">
          <span>{{ text.sections.general.total }} *</span>
          <AppMoneyInput
            id="expense-modal-amount"
            v-model="form.amount"
            output-mode="number"
            :decimal-places="2"
            :minimum-value="0"
            :currency="currencyModel"
            :disabled="isGeneralReadOnly"
          />
          <div class="invalid-feedback d-block" v-if="hasFieldError('amount')">
            <span v-if="v$.form.amount.$errors.length">
              {{ v$.form.amount.$errors[0].$message }}
            </span>
          </div>
        </label>

        <label v-if="showDiscountField" class="app-modal-field">
          <span>{{ text.sections.general.discount }}</span>
          <AppMoneyInput
            v-model="form.discount"
            output-mode="number"
            :decimal-places="2"
            :minimum-value="0"
            :currency="currencyModel"
            :disabled="isGeneralReadOnly"
          />
          <small v-if="warnings.discount" class="expense-field-warning">{{ warnings.discount }}</small>
        </label>

        <label v-if="showDeductionsField" class="app-modal-field">
          <span>{{ text.sections.general.deductions }}</span>
          <AppMoneyInput
            v-model="form.deductions"
            output-mode="number"
            :decimal-places="2"
            :minimum-value="0"
            :currency="currencyModel"
            :disabled="isGeneralReadOnly"
          />
          <small v-if="warnings.deductions" class="expense-field-warning">{{ warnings.deductions }}</small>
        </label>
      </div>

      <AppGeneralTaxesBlock
        :show-taxes-section="showTaxesSection"
        :show-tax-selectors="showTaxSelectors"
        :without-taxes-enabled="withoutTaxesEnabled"
        :disabled="isGeneralReadOnly"
        :form="form"
        :options="options"
        :text="text.sections.taxes"
        :common-text="text.sections.common"
        @toggle-without-taxes="toggleWithoutTaxes"
      />

      <AppGeneralTotalsSummary
        :text="text.sections.totals"
        :tax-preview="taxPreview"
        :show-tax-breakdown-summary="showTaxBreakdownSummary"
        :show-tax-section-breakdown="showTaxSectionBreakdown"
        :transferred-tax-items="transferredTaxItems"
        :withheld-tax-items="withheldTaxItems"
        :format-money="formatMoney"
        :format-tax-breakdown-line="formatTaxBreakdownLine"
      />
    </section>

    <ExpenseGeneralScheduleBlock
      :form="form"
      :options="options"
      :text="text.sections.schedule"
      :common-text="text.sections.common"
      :has-field-error="hasFieldError"
      :touch-field="touchField"
      :disable-dates="isGeneralReadOnly"
      :v$="v$"
    />
  </section>
</template>

<script>
import { useDomainText } from '@/composables/text/useDomainText';
import AppMoneyInput from '@/components/common/AppMoneyInput.vue';
import AppCurrencyControl from '@/components/common/AppCurrencyControl.vue';
import AppClassCategorySelector from '@/components/common/selectors/AppClassCategorySelector.vue';
import AppGeneralTaxesBlock from '@/components/common/AppGeneralTaxesBlock.vue';
import AppGeneralTotalsSummary from '@/components/common/AppGeneralTotalsSummary.vue';
import ExpenseGeneralScheduleBlock from '@/components/expenses/modal/ExpenseModalSections/ExpenseGeneralScheduleBlock.vue';
import suppliersService from '@/services/suppliers';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';
import { toPositiveOptionId } from '@/utils/optionValues';
import {
  applyExpenseFeeFrequencyRules as applyFrequencyRules,
} from '@/composables/expensesFees/shared/useExpenseFeeGeneralTransitionsShared';
import {
  buildExpenseFeeCreatedSupplierOption as buildCreatedSupplierOption,
  normalizeExpenseFeeSupplierContributorOptions as normalizeSupplierContributorOptions,
} from '@/composables/expensesFees/shared/useExpenseFeeSupplierFlow';
import {
  formatExpenseFeeMoney as formatExpenseMoney,
  formatExpenseFeeTaxBreakdownLine as formatExpenseTaxBreakdownLine,
  resolveExpenseFeeVoucherFlags as resolveExpenseVoucherFlags,
} from '@/composables/expensesFees/shared/useExpenseFeeTaxFlow';
import {
  applySupplierAutoFillFromSupplier,
  applyVoucherTypeRules,
  applyWithoutTaxesRules,
} from '@/composables/expenses/useExpenseGeneralTransitions';
import {
  cancelSupplierInlineFormShared,
  clearSupplierContributorsShared,
  loadSupplierContributorsShared,
  saveSupplierInlineShared,
  toggleSupplierInlineFormShared,
} from '@/composables/expensesFees/shared/useExpenseFeeSupplierInlineShared';

const { catalog: text } = useDomainText('expenses');

export default {
  name: 'ExpenseModalSectionGeneral',
  components: {
    AppMoneyInput,
    AppCurrencyControl,
    AppClassCategorySelector,
    AppGeneralTaxesBlock,
    AppGeneralTotalsSummary,
    ExpenseGeneralScheduleBlock,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    isExpensePaid: {
      type: Boolean,
      default: false,
    },
    statusLabel: {
      type: String,
      default: '',
    },
    isHydrating: {
      type: Boolean,
      default: false,
    },
    taxPreview: {
      type: Object,
      required: true,
    },
    taxPreviewLoading: {
      type: Boolean,
      default: false,
    },
    firstInstallmentOptions: {
      type: Array,
      default: () => [],
    },
    warnings: {
      type: Object,
      default: () => ({
        discount: '',
        deductions: '',
      }),
    },
    v$: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      text,
      showSupplierInlineForm: false,
      newSupplierName: '',
      creatingSupplier: false,
      supplierContributorsLoading: false,
      supplierContributorsRequestToken: 0,
    };
  },
  computed: {
    voucherTypeModel: {
      get() {
        const target = String(this.form?.voucherType || 'cfdi').trim().toLowerCase();
        return (Array.isArray(this.options?.documentTypes) ? this.options.documentTypes : [])
          .find((option) => String(option?.value || '').trim().toLowerCase() === target)
          || this.options?.documentTypes?.[0]
          || null;
      },
      set(option) {
        this.form.voucherType = String(option?.value || 'cfdi').trim().toLowerCase();
      },
    },
    currencyModel: {
      get() {
        const current = String(this.form?.currencyCode || '').trim().toUpperCase();
        return current !== '' ? current : 'MXN';
      },
      set(value) {
        this.form.currencyCode = String(value || '').trim().toUpperCase() || 'MXN';
      },
    },
    showSupplierContributorsSelector() {
      if (this.isSimpleNoteVoucher || this.isForeignReceiptVoucher) {
        return false;
      }

      return Array.isArray(this.options?.supplierContributors)
        && this.options.supplierContributors.length > 0;
    },
    normalizedStatusLabel() {
      return String(this.statusLabel || '').trim().toLowerCase();
    },
    isStatusPaid() {
      const normalized = this.normalizedStatusLabel;
      if (normalized === '') {
        return this.isExpensePaid;
      }

      if (
        normalized.includes('no pagad')
        || normalized.includes('unpaid')
        || normalized.includes('sin pag')
      ) {
        return false;
      }

      return normalized.includes('pagad') || normalized.includes('paid');
    },
    isGeneralReadOnly() {
      return this.isEditMode && this.isStatusPaid;
    },
    isClassCategoryReadOnly() {
      const allowPaidEditClassCategory = this.isEditMode && this.isStatusPaid;
      return this.isGeneralReadOnly && !allowPaidEditClassCategory;
    },
    pendingStatusLabel() {
      const pendingOption = (Array.isArray(this.options?.expenseStatuses) ? this.options.expenseStatuses : [])
        .find((option) => String(option?.value || '').trim().toLowerCase() === 'pending');

      const label = String(pendingOption?.label || '').trim();
      return label !== '' ? label : this.text.sections.general.defaultPendingStatus;
    },
    paidStatusLabel() {
      const paidOption = (Array.isArray(this.options?.expenseStatuses) ? this.options.expenseStatuses : [])
        .find((option) => String(option?.value || '').trim().toLowerCase() === 'paid');

      const label = String(paidOption?.label || '').trim();
      if (label !== '') {
        return label;
      }

      return String(this.text?.list?.status?.paid || '').trim() || String(this.text?.modal?.statusLabels?.paid || '').trim();
    },
    showApprovalCheckbox() {
      return !(this.isEditMode && this.isStatusPaid);
    },
    approvalLabelPrefix() {
      return 'Aprobación:';
    },
    approvalChecked() {
      return Boolean(this.form?.isApproved);
    },
    approvalStateLabel() {
      return this.approvalChecked ? 'Aprobado' : 'No aprobado';
    },
    resolvedStatusLabel() {
      const explicit = String(this.statusLabel || '').trim();
      if (explicit !== '') {
        return explicit;
      }

      return this.isExpensePaid ? this.paidStatusLabel : this.pendingStatusLabel;
    },
    voucherType() {
      return String(this.form?.voucherType || '').trim().toLowerCase();
    },
    isSimpleNoteVoucher() {
      return resolveExpenseVoucherFlags(this.voucherType).isSimpleNoteVoucher;
    },
    isForeignReceiptVoucher() {
      return resolveExpenseVoucherFlags(this.voucherType).isForeignReceiptVoucher;
    },
    isPayrollVoucher() {
      return resolveExpenseVoucherFlags(this.voucherType).isPayrollVoucher;
    },
    showTaxesSection() {
      return !this.isSimpleNoteVoucher && !this.isPayrollVoucher;
    },
    withoutTaxesEnabled() {
      return Boolean(this.form?.withoutTaxes);
    },
    showTaxSelectors() {
      return this.showTaxesSection && !this.withoutTaxesEnabled;
    },
    showTaxBreakdownSummary() {
      return !this.isSimpleNoteVoucher && !this.isPayrollVoucher && !this.withoutTaxesEnabled;
    },
    taxBreakdownItems() {
      return Array.isArray(this.taxPreview?.taxes?.items)
        ? this.taxPreview.taxes.items
        : [];
    },
    transferredTaxItems() {
      return this.taxBreakdownItems
        .filter((item) => String(item?.direction || '').trim().toLowerCase() === 'trasladado');
    },
    withheldTaxItems() {
      return this.taxBreakdownItems
        .filter((item) => String(item?.direction || '').trim().toLowerCase() === 'retenido');
    },
    showTaxSectionBreakdown() {
      return String(this.taxPreview?.tax_type || this.form?.taxType || '').trim().toLowerCase() === 'group';
    },
    showDiscountField() {
      return !this.isPayrollVoucher;
    },
    showDeductionsField() {
      return this.isPayrollVoucher;
    },
    showRecurringFields() {
      return String(this.form?.frequency || '').trim().toLowerCase() !== 'unique';
    },
    showMonthlyFields() {
      return String(this.form?.frequency || '').trim().toLowerCase() === 'monthly';
    },
    showMultipleFields() {
      return String(this.form?.frequency || '').trim().toLowerCase() === 'multiple';
    },
    selectedFrequency() {
      const frequency = String(this.form?.frequency || '').trim().toLowerCase();
      if (['unique', 'monthly', 'multiple'].includes(frequency)) {
        return frequency;
      }

      return 'unique';
    },
    frequencyCheckboxOptions() {
      return [
        { value: 'unique', label: this.text.sections.general.frequencyOptions.unique },
        { value: 'monthly', label: this.text.sections.general.frequencyOptions.monthly },
        { value: 'multiple', label: this.text.sections.general.frequencyOptions.multiple },
      ];
    },
    canOpenSupplierInlineForm() {
      const classId = toPositiveOptionId(this.form?.expenseClass);
      const categoryId = toPositiveOptionId(this.form?.expenseCategory);
      return classId > 0 && categoryId > 0;
    },
  },
  watch: {
    'form.taxType'(nextValue) {
      if (this.isHydrating) {
        return;
      }

      const mode = String(nextValue || '').trim().toLowerCase();
      if (mode === 'group') {
        this.form.taxes = [];
        return;
      }

      this.form.taxGroup = null;
    },
    'form.frequency'(nextValue) {
      applyFrequencyRules(this.form, nextValue, this.firstInstallmentOptions);
    },
    firstInstallmentOptions(nextOptions) {
      if (String(this.form?.frequency || '').trim().toLowerCase() !== 'monthly') {
        return;
      }

      applyFrequencyRules(this.form, 'monthly', nextOptions);
    },
    'form.supplier': {
      deep: true,
      handler(nextSupplier) {
        this.loadSupplierContributors(nextSupplier);
        applySupplierAutoFillFromSupplier(this.form, nextSupplier, toPositiveOptionId);
      },
    },
    voucherType: {
      immediate: true,
      handler(nextType) {
        if (this.isHydrating) {
          return;
        }

        applyVoucherTypeRules(this.form, nextType);
      },
    },
    'form.withoutTaxes'(nextValue) {
      if (this.isHydrating) {
        return;
      }

      applyWithoutTaxesRules(this.form, nextValue);
    },
  },
  methods: {
    toggleApproval(event) {
      if (this.isGeneralReadOnly) {
        return;
      }

      this.form.isApproved = Boolean(event?.target?.checked);
    },
    selectFrequency(frequencyValue) {
      if (this.isGeneralReadOnly) {
        return;
      }

      this.form.frequency = String(frequencyValue || 'unique').trim().toLowerCase();
    },
    focusSelectedFirstInstallment() {
      const selectedValue = String(this.form?.firstInstallmentOption?.value || '').trim();
      if (selectedValue === '') {
        return;
      }

      const runOnFrame = typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (callback) => setTimeout(callback, 0);

      this.$nextTick(() => {
        runOnFrame(() => {
          const selectRef = this.$refs.firstInstallmentSelect;
          const selectVm = Array.isArray(selectRef) ? selectRef[0] : selectRef;
          const rootElement = selectVm?.$el || selectVm;

          if (!rootElement || typeof rootElement.querySelector !== 'function') {
            return;
          }

          const contentWrapper = rootElement.querySelector('.multiselect__content-wrapper');
          if (!contentWrapper) {
            return;
          }

          const selectedOption = contentWrapper.querySelector('.multiselect__option--selected');
          if (selectedOption && typeof selectedOption.scrollIntoView === 'function') {
            selectedOption.scrollIntoView({ block: 'nearest' });
          }
        });
      });
    },
    toggleWithoutTaxes(event) {
      if (this.isGeneralReadOnly) {
        return;
      }

      const checked = Boolean(event?.target?.checked);
      this.form.withoutTaxes = checked;
      applyWithoutTaxesRules(this.form, checked);
    },
    touchField(fieldName) {
      if (!this.v$?.form || !this.v$.form[fieldName]) {
        return;
      }

      this.v$.form[fieldName].$touch();
    },
    hasFieldError(fieldName) {
      return Boolean(this.v$?.form?.[fieldName]?.$error);
    },
    toggleSupplierInlineForm() {
      if (this.isGeneralReadOnly) {
        return;
      }

      toggleSupplierInlineFormShared({
        state: this,
        canOpenSupplierInlineForm: this.canOpenSupplierInlineForm,
        requiresClassCategoryMessage: this.text.modal.supplierInline.requiresClassCategory,
      });
    },
    cancelSupplierInlineForm() {
      if (this.isGeneralReadOnly) {
        return;
      }

      cancelSupplierInlineFormShared(this);
    },
    clearSupplierContributors() {
      clearSupplierContributorsShared({
        state: this,
        form: this.form,
        options: this.options,
      });
    },
    async loadSupplierContributors(supplierOption) {
      await loadSupplierContributorsShared({
        state: this,
        form: this.form,
        options: this.options,
        supplierOption,
        toPositiveOptionId,
        suppliersService,
        extractCollection,
        normalizeContributorOptions: normalizeSupplierContributorOptions,
      });
    },
    async saveSupplierInline() {
      if (this.isGeneralReadOnly) {
        return;
      }

      await saveSupplierInlineShared({
        state: this,
        form: this.form,
        options: this.options,
        text: this.text,
        toPositiveOptionId,
        suppliersService,
        extractPayload,
        buildCreatedSupplierOption,
      });
    },
    formatMoney(value) {
      return formatExpenseMoney(value, this.form?.currencyCode);
    },
    formatTaxBreakdownLine(item) {
      return formatExpenseTaxBreakdownLine(item, this.form?.currencyCode);
    },
  },
};
</script>
